// Event Schedule Module
// Manages event timeline / schedule entries — mirrors the gift-registry.js pattern.

let scheduleItemCounter = 0;

// ─── Helpers ────────────────────────────────────────────────────────────────

// Returns the visible element among all elements sharing the given id.
function getActiveEl(id) {
    const els = document.querySelectorAll('[id="' + id + '"]');
    for (const el of els) {
        if (el.offsetParent !== null) return el;
    }
    return els[0] || null;
}

function updateScheduleItemCount() {
    const container = getActiveEl('scheduleItemsContainer');
    const badge     = getActiveEl('scheduleItemCount');
    if (!container || !badge) return;

    const count = container.querySelectorAll('.schedule-item').length;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'inline-block' : 'none';

    const emptyState = container.previousElementSibling;
    if (emptyState && emptyState.classList.contains('items-empty-state')) {
        emptyState.style.display = count > 0 ? 'none' : 'flex';
    }
}

// ─── Add / Remove ────────────────────────────────────────────────────────────

function addScheduleItem(itemData = null) {
    const container = getActiveEl('scheduleItemsContainer');
    if (!container) return;

    scheduleItemCounter++;
    const itemId      = itemData?.id          || `schedule_item_${scheduleItemCounter}`;
    const date        = itemData?.date        || '';
    const time        = itemData?.time        || '';
    const title       = itemData?.title       || '';
    const description = itemData?.description || '';

    const html = `
        <div class="schedule-item minimal-card" data-schedule-id="${itemId}"
             draggable="true"
             style="transition: opacity 0.3s ease, transform 0.3s ease;">

            <!-- Header row -->
            <div class="row align-items-center mb-2">
                <div class="col">
                    <span class="cursor-move text-muted" style="font-size:0.85rem;" title="Drag to reorder"><i class="bi bi-grip-vertical"></i></span>
                </div>
                <div class="col-auto">
                    <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0"
                            onclick="window.removeScheduleItem('${itemId}')">
                        <i class="bi bi-trash"></i>
                    </button>
                </div>
            </div>

            <!-- Title -->
            <div class="mb-2">
                <input type="text"
                       class="form-control form-control-sm bg-light border-0 schedule-item-title fs-5"
                       placeholder="e.g. Cocktail Hour"
                       value="${title}"
                       onchange="window.saveScheduleData()">
            </div>

            <!-- Date + Time (optional) -->
            <div class="row g-2 mb-2">
                <div class="col-7">
                    <label class="d-flex justify-content-between align-items-center schedule-item-field-label">
                        <span><i class="bi bi-calendar me-1"></i>Date</span>
                        <small class="text-muted">optional</small>
                    </label>
                    <input type="date"
                           class="form-control form-control-sm bg-light border-0 schedule-item-date"
                           value="${date}"
                           onchange="window.saveScheduleData()">
                </div>
                <div class="col-5">
                    <label class="d-flex justify-content-between align-items-center schedule-item-field-label">
                        <span><i class="bi bi-clock me-1"></i>Time</span>
                        <small class="text-muted">optional</small>
                    </label>
                    <input type="text"
                           class="form-control form-control-sm bg-light border-0 schedule-item-time"
                           placeholder="e.g. 3:00 PM"
                           value="${time}"
                           onchange="window.saveScheduleData()">
                </div>
            </div>

            <!-- Description -->
            <div>
                <label class="d-flex justify-content-between align-items-center schedule-item-field-label">
                    <span><i class="bi bi-text-left me-1"></i>Description</span>
                    <small class="text-muted">optional</small>
                </label>
                <textarea class="form-control form-control-sm bg-light border-0 schedule-item-description"
                          placeholder="Short description for this event"
                          rows="2"
                          style="resize:none;"
                          onchange="window.saveScheduleData()">${description}</textarea>
            </div>

        </div>
    `;

    container.insertAdjacentHTML('beforeend', html);
    updateScheduleItemCount();
    saveScheduleData();
}

window.addScheduleItem = addScheduleItem;

window.removeScheduleItem = function removeScheduleItem(itemId) {
    const el = document.querySelector(`[data-schedule-id="${itemId}"]`);
    if (!el) return;

    el.style.opacity   = '0';
    el.style.transform = 'translateX(-20px)';
    setTimeout(() => {
        el.remove();
        updateScheduleItemCount();
        saveScheduleData();
    }, 300);
};

// ─── Save / Load ─────────────────────────────────────────────────────────────

function saveScheduleData() {
    if (!window.formDataStorage) return;

    const items = [];
    const container = getActiveEl('scheduleItemsContainer');
    if (!container) return;
    container.querySelectorAll('.schedule-item').forEach(el => {
        const itemId      = el.getAttribute('data-schedule-id');
        const date        = (el.querySelector('.schedule-item-date')        || {}).value || '';
        const time        = (el.querySelector('.schedule-item-time')        || {}).value || '';
        const title       = (el.querySelector('.schedule-item-title')       || {}).value || '';
        const description = (el.querySelector('.schedule-item-description') || {}).value || '';

        items.push({ id: itemId, date, time, title, description });
    });

    window.formDataStorage.saveFormData({ schedule_items: items });
}

window.saveScheduleData = saveScheduleData;

function loadScheduleData() {
    if (!window.formDataStorage) return;

    const stored = window.formDataStorage.getStoredData();
    const items  = stored?.schedule_items;

    if (!Array.isArray(items) || items.length === 0) return;

    const container = getActiveEl('scheduleItemsContainer');
    if (!container) return;

    container.innerHTML    = '';
    scheduleItemCounter    = 0;

    items.forEach(item => addScheduleItem(item));
}

window.loadScheduleData = loadScheduleData;

// ─── Drag & Drop ─────────────────────────────────────────────────────────────

function setupScheduleDragAndDrop() {
    const container = getActiveEl('scheduleItemsContainer');
    if (!container) return;

    let draggedItem = null;

    // Desktop
    container.addEventListener('dragstart', function (e) {
        draggedItem = e.target.closest('.schedule-item');
        if (draggedItem) {
            draggedItem.classList.add('sortable-drag');
            draggedItem.style.opacity = '0.5';
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', draggedItem.dataset.scheduleId);
        }
    });

    container.addEventListener('dragend', function () {
        if (draggedItem) {
            draggedItem.classList.remove('sortable-drag');
            draggedItem.style.opacity = '1';
            draggedItem = null;
            saveScheduleData();
        }
    });

    container.addEventListener('dragover', function (e) {
        e.preventDefault();
        const after = getScheduleDragAfterElement(container, e.clientY);
        const dragging = container.querySelector('.sortable-drag');
        if (!dragging) return;
        if (after == null) {
            container.appendChild(dragging);
        } else {
            container.insertBefore(dragging, after);
        }
    });

    // Mobile touch
    container.addEventListener('touchstart', function (e) {
        const handle = e.target.closest('.cursor-move');
        if (!handle) return;
        e.preventDefault();
        draggedItem = handle.closest('.schedule-item');
        if (draggedItem) {
            draggedItem.classList.add('sortable-drag');
            draggedItem.style.opacity = '0.5';
        }
    }, { passive: false });

    container.addEventListener('touchmove', function (e) {
        if (!draggedItem) return;
        e.preventDefault();
        const after = getScheduleDragAfterElement(container, e.touches[0].clientY);
        if (after == null) {
            container.appendChild(draggedItem);
        } else {
            container.insertBefore(draggedItem, after);
        }
    }, { passive: false });

    container.addEventListener('touchend', function () {
        if (draggedItem) {
            draggedItem.classList.remove('sortable-drag');
            draggedItem.style.opacity = '1';
            draggedItem = null;
            saveScheduleData();
        }
    });
}

function getScheduleDragAfterElement(container, y) {
    const items = [...container.querySelectorAll('.schedule-item:not(.sortable-drag)')];
    return items.reduce((closest, child) => {
        const box    = child.getBoundingClientRect();
        const offset = y - box.top - box.height / 2;
        if (offset < 0 && offset > closest.offset) {
            return { offset, element: child };
        }
        return closest;
    }, { offset: Number.NEGATIVE_INFINITY }).element;
}

// ─── Init ──────────────────────────────────────────────────────────────────

window.setupScheduleHandlers = function setupScheduleHandlers() {
    setTimeout(() => {
        const addBtn = document.getElementById('addScheduleItem');
        if (addBtn) {
            const fresh = addBtn.cloneNode(true);
            addBtn.parentNode.replaceChild(fresh, addBtn);
            fresh.addEventListener('click', () => addScheduleItem());
        }

        const scheduleForm = document.querySelector('[data-model="schedule-form-tab"] form.edit-form');
        if (scheduleForm) {
            scheduleForm.addEventListener('submit', function (e) {
                e.preventDefault();
                saveScheduleData();

                const backBtn = document.querySelector('[data-model="schedule-form-tab"] .edit-form-back-btn');
                if (backBtn) backBtn.click();
            });
        }

        loadScheduleData();
        setupScheduleDragAndDrop();
    }, 200);
};

// Self-initialize when loaded as a standalone script (no webpack needed).
// setupGiftRegistryHandlers is defined in the invite.js bundle, so by the time
// this script tag runs the DOM and bundle are both ready.
(function () {
    function tryInit() {
        if (typeof window.setupScheduleHandlers === 'function') {
            window.setupScheduleHandlers();
        }
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', tryInit);
    } else {
        tryInit();
    }
}());
